/**
 * NumberKeypad Component
 * Ported from reference_legacy/quebon-lms-web/components/lesson/quiz-types/shared/number-keypad.tsx
 */

import { cn } from '../../../lib/utils'
import { Delete, Check } from 'lucide-react'

interface NumberKeypadProps {
  onInput: (value: string) => void
  onClear: () => void
  onSubmit?: () => void
  disabled?: boolean
  showSubmit?: boolean
  canSubmit?: boolean
}

export function NumberKeypad({
  onInput,
  onClear,
  onSubmit,
  disabled = false,
  showSubmit = false,
  canSubmit = false,
}: NumberKeypadProps) {
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0']

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg p-3 safe-area-inset-bottom">
      <div className="max-w-lg mx-auto">
        <div className="grid grid-cols-4 gap-2">
          {/* 숫자 키패드 */}
          {keys.slice(0, 3).map((key) => (
            <NumberKey key={key} value={key} onClick={onInput} disabled={disabled} />
          ))}
          <button
            onClick={onClear}
            disabled={disabled}
            className={cn(
              'h-14 rounded-xl flex items-center justify-center',
              'bg-gray-100 text-gray-600 font-semibold text-lg',
              'active:bg-gray-200 transition-colors',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <Delete className="w-6 h-6" />
          </button>

          {keys.slice(3, 6).map((key) => (
            <NumberKey key={key} value={key} onClick={onInput} disabled={disabled} />
          ))}
          <div /> {/* 빈 공간 */}

          {keys.slice(6, 9).map((key) => (
            <NumberKey key={key} value={key} onClick={onInput} disabled={disabled} />
          ))}
          
          {showSubmit ? (
            <button
              onClick={onSubmit}
              disabled={disabled || !canSubmit}
              className={cn(
                'h-14 rounded-xl flex items-center justify-center',
                'font-semibold text-lg transition-all',
                canSubmit
                  ? 'bg-emerald-500 text-white active:bg-emerald-600'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              )}
            >
              <Check className="w-6 h-6" />
            </button>
          ) : (
            <div /> 
          )}

          {/* 하단 행: 빈칸, 0, 빈칸, 빈칸 */}
          <div />
          <NumberKey value={keys[9]} onClick={onInput} disabled={disabled} />
          <div />
          <div />
        </div>
      </div>
    </div>
  )
}

interface NumberKeyProps {
  value: string
  onClick: (value: string) => void
  disabled?: boolean
}

function NumberKey({ value, onClick, disabled }: NumberKeyProps) {
  return (
    <button
      onClick={() => onClick(value)}
      disabled={disabled}
      className={cn(
        'h-14 rounded-xl flex items-center justify-center',
        'bg-white border border-gray-200 text-gray-800 font-semibold text-xl',
        'active:bg-gray-100 active:scale-95 transition-all',
        'hover:border-blue-300 hover:bg-blue-50',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {value}
    </button>
  )
}
